'use client';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import {styled, useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {usePathname} from 'next/navigation';
import {useId, useRef, useState, type KeyboardEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {DisclosureChevron, Typography} from '../../primitives';
import {isDevelopmentsActive} from '../activeNav';
import {DESKTOP_BAR_HEIGHT} from '../layout';
import {type DestinationsMenuData} from './constants';
import {MegaMenuSection} from './MegaMenuSection';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

interface DestinationsMenuProps {
  labelKey: string;
  data: DestinationsMenuData;
}

const Root = styled(Box)({
  display: 'inline-flex',
});

const Trigger = styled(ButtonBase, {shouldForwardProp: propName => propName !== 'open' && propName !== 'active'})<{
  open: boolean;
  active: boolean;
}>(({theme, open, active}) => {
  const color = active || open ? theme.palette.colors.text.primary.inverse : theme.palette.colors.text.neutral.inverse;
  return {
    gap: theme.spacing(1),
    padding: 0,
    color,
    '&:hover': {color: theme.palette.colors.text.primary.inverse},
    '&:focus-visible': {outline: `2px solid ${theme.palette.colors.border.focus.onColor}`, outlineOffset: 2},
  };
});

const Sheet = styled(Collapse)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: -1,
  pointerEvents: 'none',
});

const Panel = styled(Box)(({theme}) => ({
  paddingTop: theme.spacing(DESKTOP_BAR_HEIGHT),
  background: theme.palette.colors.surface.header.default,
}));

const Menu = styled(Container)(({theme}) => ({
  borderTop: `1px solid ${theme.palette.colors.border.neutral.strong}`,
  paddingTop: theme.spacing(3.5),
  paddingBottom: theme.spacing(5),
  pointerEvents: 'auto',
}));

const Columns = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(6.5),
}));

const Divider = styled(Box)(({theme}) => ({
  width: 1,
  alignSelf: 'stretch',
  background: theme.palette.colors.border.neutral.strong,
}));

export function DestinationsMenu({labelKey, data}: DestinationsMenuProps) {
  const {t} = useTranslation();
  const active = isDevelopmentsActive(usePathname());
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const reducedMotion = useMediaQuery(REDUCED_MOTION, {noSsr: true});

  const toggle = () => {
    setOpen(previousOpen => !previousOpen);
  };
  const close = () => {
    setOpen(false);
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      close();
      triggerRef.current?.focus();
    }
  };
  const countriesViewAllItem = {id: 'countries', name: t('countries')};
  const projectsViewAllItem = {id: 'projects', name: t('projects')};

  return (
    <ClickAwayListener onClickAway={close}>
      <Root onKeyDown={handleKeyDown}>
        <Trigger
          ref={triggerRef}
          type="button"
          disableRipple
          open={open}
          active={active}
          onClick={toggle}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
        >
          <Typography as="span" family="primary" size="body-2" weight="medium" sx={{color: 'inherit', letterSpacing: 0}}>
            {t(labelKey)}
          </Typography>
          <DisclosureChevron open={open} />
        </Trigger>
        <Sheet
          in={open}
          timeout={reducedMotion ? 0 : {enter: theme.transitions.duration.complex, exit: theme.transitions.duration.standard}}
          easing={{enter: theme.transitions.easing.easeOut, exit: theme.transitions.easing.sharp}}
          unmountOnExit
        >
          <Panel id={panelId} inert={!open || undefined}>
            <Menu>
              <Columns>
                <MegaMenuSection
                  label={t('countries')}
                  items={data.countries}
                  viewAllHref={data.countriesViewAllHref}
                  viewAllLabel={t('view_all')}
                  viewAllItem={countriesViewAllItem}
                  onNavigate={close}
                />
                {data.projects.length > 0 && (
                  <>
                    <Divider />
                    <MegaMenuSection
                      label={t('projects')}
                      items={data.projects.map(project => ({key: project.id, name: project.name, href: project.href}))}
                      viewAllHref={data.projectsViewAllHref}
                      viewAllLabel={t('view_all')}
                      viewAllItem={projectsViewAllItem}
                      onNavigate={close}
                    />
                  </>
                )}
              </Columns>
            </Menu>
          </Panel>
        </Sheet>
      </Root>
    </ClickAwayListener>
  );
}

DestinationsMenu.displayName = 'DestinationsMenu';
